import { Controller } from "@hotwired/stimulus"

/**
 * The floorplan's contents panel: what the plan holds, by zone.
 *
 * It RENDERS and nothing else. The canvas controller hands it one
 * render-ready state on `info:state` — every aisle on the plan with its zone,
 * its legs and its RackEyes, and every rack with no aisle at all with its
 * uprights; each device with its state, its IPUI and its links — and this
 * puts it into rows cloned from server-rendered `<template>`s. No word is
 * written here: every label is in the ERB, where the ten locale files can
 * reach it.
 *
 * The zones themselves — their names, their gateways, how far each one's
 * installation has got — are server-rendered into a turbo-frame, with one
 * empty slot per zone. This fills the slots, so a frame reload (the panel
 * asks for one each time it opens) is followed by a repaint into the new ones.
 *
 * Three tabs show it: Summary (the devices by state, four tiles, what the
 * plan holds), Zones (the slots) and Devices (every device once, with where
 * it is, searched and filtered here). All three are painted on every state,
 * whichever is showing, so switching tabs never waits for anything.
 */
export default class extends Controller {
  static targets = [
    "tabButton",
    "tabPanel",
    "scroller",
    "count",
    "stateSegment",
    "stateLegend",
    "tile",
    "zonesFrame",
    "unzoned",
    "unzonedRuns",
    "unzonedRacksHint",
    "unzonedBarriersHint",
    "groupHeadingTemplate",
    "unattached",
    "unattachedList",
    "empty",
    "runTemplate",
    "barrierTemplate",
    "deviceTemplate",
    "looseDeviceTemplate",
    "deviceSearch",
    "deviceFilter",
    "deviceList",
    "deviceListEmpty",
  ]

  /** data-action="info:state@document->floorplans--info#render" */
  render(event) {
    this.state = event?.detail ?? null
    this.#paint()
  }

  /** data-action="info:opened@document->floorplans--info#refresh" */
  refresh() {
    if (!this.hasZonesFrameTarget) return

    const frame = this.zonesFrameTarget
    // No `src` on first paint, so the frame does not fetch what the server
    // just rendered; the reload path is set the first time one is needed.
    if (!frame.getAttribute("src") && frame.dataset.reloadPath) {
      frame.setAttribute("src", frame.dataset.reloadPath)
    } else {
      frame.reload?.()
    }
  }

  /**
   * data-action="turbo:before-frame-render->floorplans--info#keepOpenZones"
   *
   * The zones are accordions, rendered shut, and the frame is reloaded every
   * time the panel opens — so without this, a zone somebody opened would shut
   * again each time they came back to the panel. The fresh markup is marked
   * open BEFORE it is rendered, through the accordion's own `open` value, so
   * its controller connects already open: no flash, no animation.
   */
  keepOpenZones(event) {
    const newFrame = event?.detail?.newFrame
    if (!newFrame) return

    const open = new Set(
      [...this.element.querySelectorAll("[data-info-zone]")]
        .filter(
          (zone) =>
            zone.getAttribute("data-ui--accordion-open-value") === "true",
        )
        .map((zone) => zone.dataset.infoZone),
    )

    for (const zone of newFrame.querySelectorAll("[data-info-zone]")) {
      if (!open.has(zone.dataset.infoZone)) continue

      zone.setAttribute("data-ui--accordion-open-value", "true")
      zone.dataset.state = "open"
    }

    // And FILLED before it is rendered: painted only on `turbo:frame-load`,
    // the fresh zones would show empty and at nought for a frame, every time
    // the panel opens.
    this.#paint(newFrame)
  }

  /** data-action="turbo:frame-load->floorplans--info#repaint" */
  repaint() {
    this.#paint()
  }

  /** data-action="click->floorplans--info#showTab" — one panel at a time. */
  showTab(event) {
    const tab = event.currentTarget?.dataset?.infoTab
    if (!tab) return

    for (const button of this.tabButtonTargets) {
      const active = button.dataset.infoTab === tab
      button.dataset.active = String(active)
      button.setAttribute("aria-pressed", String(active))
    }
    for (const panel of this.tabPanelTargets) {
      hideOrShow(panel, panel.dataset.infoTab !== tab)
    }
    // Each tab opens at its top, not as far down as the last one was read.
    if (this.hasScrollerTarget) this.scrollerTarget.scrollTop = 0
  }

  /**
   * data-action="input->floorplans--info#filterDevices" on the search, and
   * "click->…" on a state filter, which becomes the only one active.
   */
  filterDevices(event) {
    if (event?.type === "click") {
      const state = event.currentTarget?.dataset?.infoState ?? ""
      this.deviceFilter = state
      for (const button of this.deviceFilterTargets) {
        const active = button.dataset.infoState === state
        button.dataset.active = String(active)
        button.setAttribute("aria-pressed", String(active))
      }
    }
    this.#paintDeviceList()
  }

  /** data-action="click->floorplans--info#select" */
  select(event) {
    const entityId = event.currentTarget?.dataset?.entityId
    if (!entityId) return

    this.dispatch("select", {
      prefix: "info",
      detail: { entityId },
      bubbles: true,
    })
  }

  /**
   * `zonesRoot` is where the zones are: this panel, or — from
   * `keepOpenZones` — the frame about to replace them.
   */
  #paint(zonesRoot = this.element) {
    const state = this.state
    if (!state) return

    // A repaint replaces every row, the focused one included. Put focus back
    // on the same row, so a heartbeat does not throw a keyboard user to <body>.
    const focus = this.#rememberFocus()

    this.#paintCounts(state)

    const slots = new Map(
      [...zonesRoot.querySelectorAll("[data-info-zone-slot]")].map((slot) => [
        slot.dataset.zoneId,
        slot,
      ]),
    )
    for (const slot of slots.values()) slot.replaceChildren()

    // Each aisle under its zone; an aisle whose zone is not on this floor's
    // list, and every rack with no aisle at all, under "No zone". A barrier
    // sits in its zone the same way, or under "No zone" until one is chosen.
    // Racking first, then barriers, each list in its own group.
    const unzoned = { racking: [], barriers: [] }
    const filed = new Map()
    const tallyOf = (zoneId) => {
      // `all_devices`: RackEyes and SmartCaps in one number, as the zone's
      // header says it.
      const tally = filed.get(zoneId) ?? {
        aisles: 0,
        barriers: 0,
        all_devices: 0,
        racking: [],
        barrierRows: [],
      }
      filed.set(zoneId, tally)
      return tally
    }
    for (const aisle of state.aisles ?? []) {
      const row = this.#aisleRow(aisle)
      if (!aisle.zoneId || !slots.has(aisle.zoneId)) {
        unzoned.racking.push(row)
        continue
      }

      const tally = tallyOf(aisle.zoneId)
      tally.racking.push(row)
      tally.aisles += 1
      tally.all_devices += aisle.devices?.length ?? 0
    }
    for (const rack of state.racks ?? []) {
      unzoned.racking.push(this.#rackRow(rack))
    }
    // A floor with a single zone puts every barrier in it: its panel offers no
    // zone to choose there (`BarrierPropertiesComponent#zone_choice?`).
    const onlyZone = slots.size === 1 ? [...slots.keys()][0] : null
    for (const barrier of state.barriers ?? []) {
      const row = this.#barrierRow(barrier)
      if (!row) continue
      const zoneId =
        barrier.zoneId && slots.has(barrier.zoneId) ? barrier.zoneId : onlyZone
      if (!zoneId) {
        unzoned.barriers.push(row)
        continue
      }

      const tally = tallyOf(zoneId)
      tally.barrierRows.push(row)
      tally.barriers += 1
      tally.all_devices += barrier.devices?.length ?? 0
    }
    for (const [zoneId, tally] of filed) {
      slots
        .get(zoneId)
        .append(...this.#grouped("aisles", tally.racking, tally.barrierRows))
    }

    // A shut zone still says how much is inside it, so somebody knows which
    // one to open. A barrier count is optional: shown only once there is one.
    for (const zone of zonesRoot.querySelectorAll("[data-info-zone]")) {
      const tally = filed.get(zone.dataset.infoZone)
      for (const count of zone.querySelectorAll("[data-info-zone-count]")) {
        const n = tally?.[count.dataset.infoZoneCount] ?? 0
        const value = count.querySelector("[data-info-zone-value]")
        if (value) value.textContent = String(n)
        if (count.hasAttribute("data-info-zone-optional")) {
          hideOrShow(count, n === 0)
        }
      }
    }

    if (this.hasUnzonedRunsTarget) {
      this.unzonedRunsTarget.replaceChildren(
        ...this.#grouped("runs", unzoned.racking, unzoned.barriers),
      )
    }
    const anyUnzoned = unzoned.racking.length + unzoned.barriers.length > 0
    if (this.hasUnzonedTarget) hideOrShow(this.unzonedTarget, !anyUnzoned)
    // Why each is here, said for the kinds that are.
    if (this.hasUnzonedRacksHintTarget) {
      hideOrShow(this.unzonedRacksHintTarget, !unzoned.racking.length)
    }
    if (this.hasUnzonedBarriersHintTarget) {
      hideOrShow(this.unzonedBarriersHintTarget, !unzoned.barriers.length)
    }

    const loose = (state.unattached ?? []).map((device) =>
      this.#looseDeviceRow(device),
    )
    if (this.hasUnattachedListTarget) {
      this.unattachedListTarget.replaceChildren(...loose)
    }
    if (this.hasUnattachedTarget) {
      hideOrShow(this.unattachedTarget, !loose.length)
    }

    if (this.hasEmptyTarget) {
      const listed =
        (state.aisles?.length ?? 0) +
        (state.racks?.length ?? 0) +
        (state.barriers?.length ?? 0) +
        loose.length
      hideOrShow(this.emptyTarget, listed > 0)
    }

    this.devices = this.#everyDevice(state, slots, zonesRoot)
    this.#paintDeviceFilters()
    this.#paintDeviceList()

    this.#restoreFocus(focus)
  }

  /**
   * Every device on the plan once, each with where it is in words — its
   * zone, its aisle (or rack, or barrier) and its leg or post — for the
   * Devices tab to list and search. A loose one is fitted to nothing, so it
   * has nowhere to say.
   *
   * Which zone is decided from `slots`, by the same rules `#paint` files a
   * row by, so a device's words can never name a zone its row is not in; the
   * zone's element only lends its name.
   */
  #everyDevice(state, slots, zonesRoot) {
    const unzoned = this.hasDeviceListTarget
      ? (this.deviceListTarget.dataset.unzoned ?? "")
      : ""
    const zoneNames = new Map(
      [...zonesRoot.querySelectorAll("[data-info-zone]")].map((zone) => [
        zone.dataset.infoZone,
        zone.dataset.infoZoneName,
      ]),
    )
    const filed = (zoneId) => (zoneId && slots.has(zoneId) ? zoneId : null)
    const zoneOf = (zoneId) => (zoneId && zoneNames.get(zoneId)) || unzoned
    const onlyZone = slots.size === 1 ? [...slots.keys()][0] : null

    return [
      ...(state.aisles ?? []).flatMap((aisle) =>
        (aisle.devices ?? []).map((device) => ({
          device,
          where: [zoneOf(filed(aisle.zoneId)), aisle.name, device.pointLabel],
        })),
      ),
      ...(state.racks ?? []).flatMap((rack) =>
        (rack.devices ?? []).map((device) => ({
          device,
          where: [unzoned, rack.name, device.pointLabel],
        })),
      ),
      ...(state.barriers ?? []).flatMap((barrier) =>
        (barrier.devices ?? []).map((device) => ({
          device,
          // In the floor's only zone when none of this floor's was chosen.
          where: [
            zoneOf(filed(barrier.zoneId) ?? onlyZone),
            barrier.name || barrier.productName,
            device.pointLabel,
          ],
          onPost: true,
        })),
      ),
      ...(state.unattached ?? []).map((device) => ({ device, where: [] })),
    ]
  }

  /** Each state's filter says how many it has, and shows only while some do. */
  #paintDeviceFilters() {
    const devices = this.devices ?? []
    for (const button of this.deviceFilterTargets) {
      const state = button.dataset.infoState
      const n = state
        ? devices.filter(({ device }) => device.state === state).length
        : devices.length
      const value = button.querySelector("[data-info-state-count]")
      if (value) value.textContent = String(n)
      if (state) hideOrShow(button, n === 0)
    }
    // The state filtered on has no device left: back to all of them.
    if (
      this.deviceFilter &&
      !devices.some(({ device }) => device.state === this.deviceFilter)
    ) {
      this.deviceFilter = ""
      for (const button of this.deviceFilterTargets) {
        const active = button.dataset.infoState === ""
        button.dataset.active = String(active)
        button.setAttribute("aria-pressed", String(active))
      }
    }
  }

  /** The devices the state filter and the search leave, as cards. */
  #paintDeviceList() {
    if (!this.hasDeviceListTarget || !this.devices) return

    const state = this.deviceFilter ?? ""
    const query = this.hasDeviceSearchTarget
      ? this.deviceSearchTarget.value.trim().toLowerCase()
      : ""
    const shown = this.devices.filter(({ device, where }) => {
      if (state && device.state !== state) return false
      if (!query) return true
      return [device.ipui, device.name, ...where]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(query))
    })

    this.deviceListTarget.replaceChildren(
      ...shown.map(({ device, where, onPost }) => {
        const node = this.#deviceRow(device, { onPost })
        node.querySelector("[data-info-device-where]").textContent = where
          .filter(Boolean)
          .join(" · ")
        return node
      }),
    )
    if (this.hasDeviceListEmptyTarget) {
      hideOrShow(this.deviceListEmptyTarget, shown.length > 0)
    }
  }

  /**
   * Racking rows, then barrier rows — each under a heading when a list holds
   * both, so a barrier is never read as one more aisle. `racking` names the
   * first group: "aisles" in a zone, "runs" under "No zone".
   */
  #grouped(racking, rackingRows, barrierRows) {
    if (
      !rackingRows.length ||
      !barrierRows.length ||
      !this.hasGroupHeadingTemplateTarget
    ) {
      return [...rackingRows, ...barrierRows]
    }

    const heading = (key) => {
      const node =
        this.groupHeadingTemplateTarget.content.firstElementChild.cloneNode(
          true,
        )
      node.textContent = node.dataset[key] ?? ""
      return node
    }
    return [
      heading(racking),
      ...rackingRows,
      heading("barriers"),
      ...barrierRows,
    ]
  }

  /** Which row had focus, and on what in it: its select button or a link. */
  #rememberFocus() {
    const active = document.activeElement
    if (!(active instanceof HTMLElement) || !this.element.contains(active)) {
      return null
    }

    const row = active.closest("li")
    const entityId =
      active.dataset.entityId ??
      row?.querySelector("[data-entity-id]")?.dataset.entityId
    if (!entityId) return null

    const part = FOCUSABLE_PARTS.find((selector) => active.matches(selector))
    // A device is listed twice — in its zone and in the Devices tab — so the
    // row to go back to is the one in the same tab.
    const panel = active.closest("[data-info-tab]")
    return part ? { entityId, part, panel } : null
  }

  #restoreFocus(focus) {
    if (!focus) return
    if (this.element.contains(document.activeElement)) return

    const scope =
      focus.panel && this.element.contains(focus.panel)
        ? focus.panel
        : this.element
    const select = [...scope.querySelectorAll("[data-entity-id]")].find(
      (el) => el.dataset.entityId === focus.entityId,
    )
    const target =
      focus.part === SELECT_PART
        ? select
        : select?.closest("li")?.querySelector(focus.part)
    target?.focus()
  }

  /**
   * The summary. Its aisles are the ones listed under the zones — every face
   * facilities files — not the canvas's racking runs. A device is a RackEye
   * or a SmartCap alike: the tile counts both.
   */
  #paintCounts(state) {
    const counts = { ...state.counts, aisles: state.aisles?.length ?? 0 }
    for (const el of this.countTargets) {
      el.textContent = String(counts[el.dataset.infoCount] ?? 0)
    }

    // Each state's share of the bar, and its word and number under it —
    // only the states some device is in.
    const byState = counts.byState ?? {}
    for (const segment of this.stateSegmentTargets) {
      const n = byState[segment.dataset.infoState] ?? 0
      segment.style.flexGrow = String(n)
      hideOrShow(segment, n === 0)
    }
    for (const item of this.stateLegendTargets) {
      const n = byState[item.dataset.infoState] ?? 0
      const value = item.querySelector("[data-info-state-count]")
      if (value) value.textContent = String(n)
      hideOrShow(item, n === 0)
    }

    const tiles = {
      devices: (counts.devices ?? 0) + (counts.smartcaps ?? 0),
      online: byState.online ?? 0,
      attention: (byState.alert ?? 0) + (byState.warning ?? 0),
      offline: byState.offline ?? 0,
    }
    for (const tile of this.tileTargets) {
      tile.textContent = String(tiles[tile.dataset.infoTile] ?? 0)
    }
  }

  /** An aisle: its name, its legs, its RackEyes. Selecting it selects its rack. */
  #aisleRow(aisle) {
    const node = this.#row(aisle.runId, aisle.devices)
    node.querySelector("[data-info-run-name]").textContent = aisle.name || ""
    this.#count(node, "legs", aisle.legs)
    return node
  }

  /**
   * A rack with no aisle on either face — drawn by hand, or left when its
   * aisles went. It has no legs to count, only uprights.
   */
  #rackRow(rack) {
    const node = this.#row(rack.runId, rack.devices)
    const name = node.querySelector("[data-info-run-name]")
    name.textContent = rack.name || name.dataset.unnamed || ""
    this.#count(node, "uprights", rack.uprights)
    return node
  }

  /** A barrier: its name (or its product's), its posts, its SmartCaps. */
  #barrierRow(barrier) {
    if (!this.hasBarrierTemplateTarget) return null
    const node =
      this.barrierTemplateTarget.content.firstElementChild.cloneNode(true)
    node.querySelector("[data-info-run-select]").dataset.entityId = barrier.id
    node.querySelector("[data-info-run-name]").textContent =
      barrier.name || barrier.productName || ""
    node.querySelector("[data-info-barrier-posts]").textContent = String(
      barrier.posts ?? 0,
    )
    node.querySelector("[data-info-run-devices]").textContent = String(
      barrier.devices?.length ?? 0,
    )

    const list = node.querySelector("[data-info-run-device-list]")
    list.replaceChildren(
      ...(barrier.devices ?? []).map((d) =>
        this.#deviceRow(d, { onPost: true }),
      ),
    )
    hideOrShow(list, !(barrier.devices?.length > 0))
    return node
  }

  #row(runId, devices) {
    const node =
      this.runTemplateTarget.content.firstElementChild.cloneNode(true)
    node.querySelector("[data-info-run-select]").dataset.entityId = runId
    node.querySelector("[data-info-run-devices]").textContent = String(
      devices?.length ?? 0,
    )

    const list = node.querySelector("[data-info-run-device-list]")
    list.replaceChildren(...(devices ?? []).map((d) => this.#deviceRow(d)))
    hideOrShow(list, !(devices?.length > 0))
    return node
  }

  /** Show one of a row's counts — legs or uprights — and drop the other. */
  #count(node, which, value) {
    for (const count of node.querySelectorAll("[data-info-run-count]")) {
      const shown = count.dataset.infoRunCount === which
      hideOrShow(count, !shown)
      if (shown) {
        count.querySelector("[data-info-run-value]").textContent = String(
          value ?? 0,
        )
      }
    }
  }

  /** `onPost` — a SmartCap on a barrier's post, not a RackEye on a leg. */
  #deviceRow(device, { onPost = false } = {}) {
    const node =
      this.deviceTemplateTarget.content.firstElementChild.cloneNode(true)

    node.querySelector("[data-info-device-select]").dataset.entityId =
      device.entityId
    node.querySelector("[data-device-state-dot]").dataset.state = device.state

    // WHICH UNIT: the IPUI once one is installed, the product until then.
    const label = node.querySelector("[data-info-device-label]")
    label.textContent = device.ipui || device.name || ""
    label.classList.toggle("font-mono", Boolean(device.ipui))

    // WHERE: the leg — after its face's word for a RackEye on a face with no
    // aisle, listed under the other face's.
    const where = node.querySelector("[data-info-device-where]")
    where.textContent = [
      device.withFace ? where.dataset[device.side] || "" : "",
      device.pointLabel,
    ]
      .filter(Boolean)
      .join(" · ")

    for (const word of node.querySelectorAll("[data-info-device-state]")) {
      hideOrShow(word, word.dataset.infoDeviceState !== device.state)
    }

    const assetLink = node.querySelector("[data-info-device-asset-link]")
    // Its asset is a post: the link says so, not "leg".
    if (onPost && assetLink.dataset.postLabel) {
      assetLink.title = assetLink.dataset.postLabel
      assetLink.setAttribute("aria-label", assetLink.dataset.postLabel)
    }
    setLink(assetLink, device.assetHref)
    setLink(
      node.querySelector("[data-info-device-sensor-link]"),
      device.sensorHref,
    )

    return node
  }

  #looseDeviceRow(device) {
    const node =
      this.looseDeviceTemplateTarget.content.firstElementChild.cloneNode(true)

    node.querySelector("[data-info-device-select]").dataset.entityId =
      device.entityId
    node.querySelector("[data-device-state-dot]").dataset.state = device.state
    node.querySelector("[data-info-device-label]").textContent =
      device.name || ""

    return node
  }
}

/** The parts of a row focus can be on, as the row templates mark them. */
const SELECT_PART = "[data-entity-id]"
const FOCUSABLE_PARTS = [
  SELECT_PART,
  "[data-info-device-asset-link]",
  "[data-info-device-sensor-link]",
]

/**
 * Point a link somewhere, or hide it: a link that goes nowhere is worse than
 * none. Hidden IN PLACE — `visibility`, not `display` — so the column of
 * links keeps its width and every card's state word lines up with the next.
 * An invisible link is out of the tab order and the accessibility tree too.
 */
function setLink(link, href) {
  if (!link) return

  link.style.visibility = href ? "" : "hidden"
  if (href) link.href = href
}

/**
 * Show or hide an element that may also carry a display utility — the
 * attribute and an inline `display` together, as the canvas controller does.
 */
function hideOrShow(element, hide) {
  element.hidden = hide
  element.style.display = hide ? "none" : ""
}
